/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Injectable, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';

/**
 * File node data with nested structure.
 * Each node has a filename, and a type or a list of children.
 */
export class FileNode {
	id: string;
	children: FileNode[];
	key: string;
	value: any;
}

/** Flat node with expandable and level information */
export class FileFlatNode {
	constructor(
		public expandable: boolean,
		public key: string,
		public level: number,
		public value: any,
		public id: string
	) { }
}

/**
 * File database, it can build a tree structured Json object from string.
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Injectable()
export class FileDatabase {
	TREE_DATA = '';
	_dataAdded = new BehaviorSubject<string>('');
	dataChange = new BehaviorSubject<FileNode[]>([]);

	constructor() {
		this._dataAdded.subscribe((data) => {
			this.initialize(data);
		});
	}

	get data(): FileNode[] { return this.dataChange.value; }
	get dataAdded(): string { return this.dataAdded; }

	setDataAdded(data: string) {
		this._dataAdded.next(data);
	}

	initialize(dataAdded: string) {
		const dataObject = JSON.parse(dataAdded);

		// Build the tree nodes from Json object. The result is a list of `FileNode` with nested
		//     file node as children.
		const data = this.buildFileTree(dataObject, 0);
		for(const node of data) {
			if (node.children?.length === 0) {
				delete node.children;
				node.value = '[ ]';
			}
		}

		// Notify the change.
		this.dataChange.next(data);
	}

	/**
	 * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
	 * The return value is the list of `FileNode`.
	 */
	buildFileTree(obj: { [key: string]: any }, level: number, parentId: string = '0'): FileNode[] {
		return Object.keys(obj).reduce<FileNode[]>((accumulator, key, idx) => {
			const value = obj[key];
			const node = new FileNode();
			node.key = key;
			/**
			 * Make sure your node has an id so we can properly rearrange the tree during drag'n'drop.
			 * By passing parentId to buildFileTree, it constructs a path of indexes which make
			 * it possible find the exact sub-array that the node was grabbed from when dropped.
			 */
			node.id = `${parentId}/${idx}`;

			if (value != null) {
				if (typeof value === 'object') {
					node.children = this.buildFileTree(value, level + 1, node.id);
				} else {
					node.value = value;
				}
			}

			return accumulator.concat(node);
		}, []);
	}
}

/**
 * @title Tree with flat nodes
 */
@Component({
    selector: 'dynamic-tree',
    templateUrl: './dynamic-tree.component.html',
    styleUrls: ['./dynamic-tree.component.scss'],
    providers: [FileDatabase],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DynamicTreeComponent implements OnInit {
	@Input() data: any;
	treeControl: FlatTreeControl<FileFlatNode>;
	treeFlattener: MatTreeFlattener<FileNode, FileFlatNode>;
	dataSource: MatTreeFlatDataSource<FileNode, FileFlatNode>;
	// expansion model tracks expansion state
	expansionModel = new SelectionModel<string>(true);
	dragging = false;
	expandTimeout: any;
	expandDelay = 1000;
	validateDrop = false;
	database: FileDatabase;

	constructor(database: FileDatabase) {
		this.database = database;
	}

	ngOnInit(): void {
		this.database.setDataAdded(JSON.stringify(this.data));
		this.treeFlattener = new MatTreeFlattener(this.transformer, this._getLevel,
			this._isExpandable, this._getChildren);
		this.treeControl = new FlatTreeControl<FileFlatNode>(this._getLevel, this._isExpandable);
		this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

		this.database.dataChange.subscribe(data => this.rebuildTreeForData(data));
	}

	transformer = (node: FileNode, level: number) => new FileFlatNode(!!node.children, node.key, level, node.value, node.id);
	hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;


	/**
	 * This constructs an array of nodes that matches the DOM
	 */
	visibleNodes(): FileNode[] {
		const result = [];

		function addExpandedChildren(node: FileNode, expanded: string[]) {
			result.push(node);
			if (expanded.includes(node.id)) {
				node.children.map(child => addExpandedChildren(child, expanded));
			}
		}
		this.dataSource.data.forEach((node) => {
			addExpandedChildren(node, this.expansionModel.selected);
		});
		return result;
	}

	/**
	 * The following methods are for persisting the tree expand state
	 * after being rebuilt
	 */

	rebuildTreeForData(data: any) {
		data = this.deleteEmptyArrays(data);
		this.dataSource.data = data;
		this.expansionModel.selected.forEach((id) => {
			const node = this.treeControl.dataNodes.find(n => n.id === id);
			this.treeControl.expand(node);
		});
	}

	deleteEmptyArrays(data: FileNode[]) {
		for (const node of data) {
			if (node.children?.length > 0) {
				this.deleteEmptyArrays(node.children);
			} else if (node.children?.length === 0) {
				delete node.children;
				node.value = '[ ]';
			}
		}
		return data;
	}

	private _getLevel = (node: FileFlatNode) => node.level;
	private _isExpandable = (node: FileFlatNode) => node.expandable;
	private _getChildren = (node: FileNode): Observable<FileNode[]> => observableOf(node.children);


}
